// src/i18n.js
import {
  GetStorefrontScansLocationEnum,
  GetStorefrontScansTagEnum,
  InventoryDTOInventoryStatusEnum,
  ListSearchesStatusesEnum,
  MarketplaceDTOMarketplaceEnum,
  SearchResultRequestDTODecisionEnum,
} from '@sellsen/generated-sources';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Translation resources
const resources = {
  en: {
    translation: {
      welcome: 'Welcome',
      description: 'This is an example',
      // Banned Categories Page
      'bannedCategories.categoryName': 'Category Name',
      'bannedCategories.marketplace': 'Marketplace',
      'bannedCategories.createdAt': 'Created At',
      'bannedCategories.action': 'Action',
      'bannedCategories.remove': 'Remove',
      'bannedCategories.removeConfirm':
        'Are you sure you want to remove this category?',
      'bannedCategories.removeSelectedConfirm':
        'Are you sure you want to remove {{count}} selected categories?',
      'bannedCategories.removeSelected': 'Remove Selected',
      'bannedCategories.yes': 'Yes',
      'bannedCategories.no': 'No',
      'bannedCategories.filterByMarketplace': 'Filter by marketplace',
      'bannedCategories.searchPlaceholder': 'Search category',
      'bannedCategories.addCategories': 'Add Categories',
      'bannedCategories.totalBannedCategories':
        'Total {{total}} banned categories',
      'bannedCategories.addBannedCategoriesInBulk':
        'Add Banned Categories in Bulk',
      'bannedCategories.categoriesOnePerLine': 'Categories',
      'bannedCategories.categorySelectionHint':
        'A main category bans that department. A subcategory bans the full path under it, including deeper products. The same name under another department stays allowed.',
      'bannedCategories.unmappedTitle': 'Entries that are not on the tree',
      'bannedCategories.unmappedHint':
        'These were saved as free text, so they are not checked on the tree. Removing one lifts that ban. The change is kept until you save.',
      'bannedCategories.save': 'Save',
      'bannedCategories.discard': 'Discard',
      'bannedCategories.saveHint': 'Checks stay on the page until you save.',
      'bannedCategories.unsavedChanges':
        '{{added}} to add, {{removed}} to remove',
      'bannedCategories.saved': 'Saved',
      'bannedCategories.commonPoolHint':
        'Save updates the shared pool for this marketplace.',
      'bannedCategories.commonPoolReadOnlyHint':
        'This shared pool is view-only. Copy categories into your own banned list to apply them.',
      'bannedCategories.enterAtLeastOneCategory':
        'Please enter at least one category',
      'bannedCategories.bulkLimit':
        'You can add at most {{max}} items at once',
      'bannedCategories.categoryPlaceholder':
        'Enter category names, one per line\nExample:\nElectronics\nWeapons\nProhibited items',
      'bannedCategories.selectMarketplaces': 'Select Marketplaces',
      'bannedCategories.selectAtLeastOneMarketplace':
        'Please select at least one marketplace',
      'bannedCategories.selectMarketplacesPlaceholder':
        'Select marketplaces to apply these categories',
      'bannedCategories.cancel': 'Cancel',
      'bannedCategories.successfullyAdded': '{{count}} categories added',
      'bannedCategories.failedToAdd': 'Failed to add categories',
      'bannedCategories.categoryRemovedSuccessfully':
        'Category removed successfully',
      'bannedCategories.failedToRemoveCategory': 'Failed to remove category',
      'bannedCategories.selectCategoriesToDelete':
        'Please select categories to delete',
      'bannedCategories.successfullyRemoved':
        '{{count}} categories successfully removed',
      'bannedCategories.failedToRemoveCategories':
        'Failed to remove categories',
      // Banned Brands Page
      'bannedBrands.categoryName': 'Brand Name',
      'bannedBrands.marketplace': 'Marketplace',
      'bannedBrands.createdAt': 'Created At',
      'bannedBrands.action': 'Action',
      'bannedBrands.remove': 'Remove',
      'bannedBrands.removeConfirm':
        'Are you sure you want to remove this brand?',
      'bannedBrands.removeSelectedConfirm':
        'Are you sure you want to remove {{count}} selected brands?',
      'bannedBrands.removeSelected': 'Remove Selected',
      'bannedBrands.yes': 'Yes',
      'bannedBrands.no': 'No',
      'bannedBrands.filterByMarketplace': 'Filter by marketplace',
      'bannedBrands.searchPlaceholder': 'Search brand',
      'bannedBrands.addCategories': 'Add Brands',
      'bannedBrands.totalBannedCategories': 'Total {{total}} banned brands',
      'bannedBrands.addBannedCategoriesInBulk': 'Add Banned Brands in Bulk',
      'bannedBrands.categoriesOnePerLine': 'Brands (one per line)',
      'bannedBrands.enterAtLeastOneCategory': 'Please enter at least one brand',
      'bannedBrands.bulkLimit': 'You can add at most {{max}} items at once',
      'bannedBrands.categoryPlaceholder':
        'Enter brand names, one per line\nExample:\nSony\nApple\nNike',
      'bannedBrands.selectMarketplaces': 'Select Marketplaces',
      'bannedBrands.selectAtLeastOneMarketplace':
        'Please select at least one marketplace',
      'bannedBrands.selectMarketplacesPlaceholder':
        'Select marketplaces to apply these brands',
      'bannedBrands.cancel': 'Cancel',
      'bannedBrands.successfullyAdded': '{{count}} brands added',
      'bannedBrands.failedToAdd': 'Failed to add brands',
      'bannedBrands.categoryRemovedSuccessfully': 'Brand removed successfully',
      'bannedBrands.failedToRemoveCategory': 'Failed to remove brand',
      'bannedBrands.selectCategoriesToDelete': 'Please select brands to delete',
      'bannedBrands.successfullyRemoved':
        '{{count}} brands successfully removed',
      'bannedBrands.failedToRemoveCategories': 'Failed to remove brands',
      // Banned ASIN Page
      'bannedAsin.categoryName': 'ASIN',
      'bannedAsin.marketplace': 'Marketplace',
      'bannedAsin.createdAt': 'Created At',
      'bannedAsin.action': 'Action',
      'bannedAsin.remove': 'Remove',
      'bannedAsin.removeConfirm': 'Are you sure you want to remove this ASIN?',
      'bannedAsin.removeSelectedConfirm':
        'Are you sure you want to remove {{count}} selected ASINs?',
      'bannedAsin.removeSelected': 'Remove Selected',
      'bannedAsin.yes': 'Yes',
      'bannedAsin.no': 'No',
      'bannedAsin.filterByMarketplace': 'Filter by marketplace',
      'bannedAsin.searchPlaceholder': 'Search ASIN',
      'bannedAsin.addCategories': 'Add ASINs',
      'bannedAsin.totalBannedCategories': 'Total {{total}} banned ASINs',
      'bannedAsin.addBannedCategoriesInBulk': 'Add Banned ASINs in Bulk',
      'bannedAsin.categoriesOnePerLine': 'ASINs (one per line)',
      'bannedAsin.enterAtLeastOneCategory': 'Please enter at least one ASIN',
      'bannedAsin.bulkLimit': 'You can add at most {{max}} items at once',
      'bannedAsin.categoryPlaceholder':
        'Enter ASIN codes, one per line\nExample:\nB08XYZ1234\nB09ABC5678\nB10DEF9012',
      'bannedAsin.selectMarketplaces': 'Select Marketplaces',
      'bannedAsin.selectAtLeastOneMarketplace':
        'Please select at least one marketplace',
      'bannedAsin.selectMarketplacesPlaceholder':
        'Select marketplaces to apply these ASINs',
      'bannedAsin.cancel': 'Cancel',
      'bannedAsin.successfullyAdded': '{{count}} ASINs added',
      'bannedAsin.failedToAdd': 'Failed to add ASINs',
      'bannedAsin.categoryRemovedSuccessfully': 'ASIN removed successfully',
      'bannedAsin.failedToRemoveCategory': 'Failed to remove ASIN',
      'bannedAsin.selectCategoriesToDelete': 'Please select ASINs to delete',
      'bannedAsin.successfullyRemoved': '{{count}} ASINs successfully removed',
      'bannedAsin.failedToRemoveCategories': 'Failed to remove ASINs',
      // Banned Keywords Page
      'bannedKeyword.categoryName': 'Keyword',
      'bannedKeyword.marketplace': 'Marketplace',
      'bannedKeyword.createdAt': 'Created At',
      'bannedKeyword.action': 'Action',
      'bannedKeyword.remove': 'Remove',
      'bannedKeyword.removeConfirm':
        'Are you sure you want to remove this keyword?',
      'bannedKeyword.removeSelectedConfirm':
        'Are you sure you want to remove {{count}} selected keywords?',
      'bannedKeyword.removeSelected': 'Remove Selected',
      'bannedKeyword.yes': 'Yes',
      'bannedKeyword.no': 'No',
      'bannedKeyword.filterByMarketplace': 'Filter by marketplace',
      'bannedKeyword.searchPlaceholder': 'Search keyword',
      'bannedKeyword.addCategories': 'Add Keywords',
      'bannedKeyword.totalBannedCategories': 'Total {{total}} banned keywords',
      'bannedKeyword.addBannedCategoriesInBulk': 'Add Banned Keywords in Bulk',
      'bannedKeyword.categoriesOnePerLine': 'Keywords (one per line)',
      'bannedKeyword.enterAtLeastOneCategory':
        'Please enter at least one keyword',
      'bannedKeyword.bulkLimit': 'You can add at most {{max}} items at once',
      'bannedKeyword.categoryPlaceholder':
        'Enter keywords, one per line\nExample:\nweapon\nknife\nsword',
      'bannedKeyword.selectMarketplaces': 'Select Marketplaces',
      'bannedKeyword.selectAtLeastOneMarketplace':
        'Please select at least one marketplace',
      'bannedKeyword.selectMarketplacesPlaceholder':
        'Select marketplaces to apply these keywords',
      'bannedKeyword.cancel': 'Cancel',
      'bannedKeyword.successfullyAdded': '{{count}} keywords added',
      'bannedKeyword.failedToAdd': 'Failed to add keywords',
      'bannedKeyword.categoryRemovedSuccessfully':
        'Keyword removed successfully',
      'bannedKeyword.failedToRemoveCategory': 'Failed to remove keyword',
      'bannedKeyword.selectCategoriesToDelete':
        'Please select keywords to delete',
      'bannedKeyword.successfullyRemoved':
        '{{count}} keywords successfully removed',
      'bannedKeyword.failedToRemoveCategories': 'Failed to remove keywords',
      // Storefront Page Tour (EN)
      'storefront.tour.start': 'Page tour',
      'storefront.tour.filters.title': 'Filters',
      'storefront.tour.filters.desc':
        'Use the filters to narrow down storefront scans by location, tag and more.',
      'storefront.tour.savedFilters.title': 'Saved Filters',
      'storefront.tour.savedFilters.desc':
        'Quickly apply or manage your saved filter presets.',
      'storefront.tour.newSearch.title': 'New Search',
      'storefront.tour.newSearch.desc':
        'Create a new storefront scan. You can track its progress here.',
      'storefront.tour.bulkActions.title': 'Bulk Actions',
      'storefront.tour.bulkActions.desc':
        'Copy, download, mark exported, re-scan, add to pool, or delete for selected items.',
      'storefront.tour.table.title': 'Results Table',
      'storefront.tour.table.desc':
        'View storefront scans, open shop pages, and use row actions from here.',
      // Table pagination (EN)
      'table.pagination.total': 'Total {{total}} items',
      // Search By Keyword Page Tour (EN)
      'searchByKeyword.tour.start': 'Page tour',
      'searchByKeyword.tour.filters.title': 'Filters',
      'searchByKeyword.tour.filters.desc':
        'Filter keyword scans by country and other parameters.',
      'searchByKeyword.tour.savedFilters.title': 'Saved Filters',
      'searchByKeyword.tour.savedFilters.desc':
        'Apply or manage your saved filter presets.',
      'searchByKeyword.tour.newSearch.title': 'New Search',
      'searchByKeyword.tour.newSearch.desc':
        'Create a new keyword scan and follow its progress here.',
      'searchByKeyword.tour.bulkActions.title': 'Bulk Actions',
      'searchByKeyword.tour.bulkActions.desc':
        'Copy, download, mark exported, re-scan, add to pool, or delete selections.',
      'searchByKeyword.tour.table.title': 'Results Table',
      'searchByKeyword.tour.table.desc':
        'See keyword scan results and use row actions.',
      // Marketplace Filter Setting Page
      'marketplaceFilterSetting.sourceFilters': 'Source Filters',
      'marketplaceFilterSetting.targetCriteria': 'Target Criteria',
      'marketplaceFilterSetting.save': 'Save',
      'marketplaceFilterSetting.saveSuccess':
        'Configuration saved successfully!',
      'marketplaceFilterSetting.saveError': 'Failed to save configuration',
      'marketplaceFilterSetting.selectMarketplace':
        'Please select a marketplace',
      'marketplaceFilterSetting.source.POSSIBLE_DIGITAL_PRODUCT':
        'Possible Digital Product',
      'marketplaceFilterSetting.source.UNKNOWN_SHIPPING_FEE':
        'Unknown Shipping Fee',
      'marketplaceFilterSetting.source.TRADEMARKED_PRODUCTS':
        'Trademarked Products',
      'marketplaceFilterSetting.source.SBA_TAGGED': 'SBA Tagged',
      'marketplaceFilterSetting.source.FBA_TAGGED': 'FBA Tagged',
      'marketplaceFilterSetting.source.AMAZON_REMOVED_PRODUCTS':
        'Amazon Removed Products',
      'marketplaceFilterSetting.source.UNSHIPPABLE_PRODUCTS':
        'Unshippable Products',
      'marketplaceFilterSetting.source.NON_RETURNABLE': 'Non Returnable',
      'marketplaceFilterSetting.source.FBM_TAGGED': 'FBM Tagged',
      'marketplaceFilterSetting.source.BRAND_AND_SELLER_SAME':
        'Brand and Seller Same',
      'marketplaceFilterSetting.source.BANNED': 'Banned',
      'marketplaceFilterSetting.source.COMPETITION_STRONG':
        'Strong Competition',
      'marketplaceFilterSetting.source.UNQUALIFIED_PRODUCT':
        'Unqualified Product',
      'marketplaceFilterSetting.source.NO_DIRECT_SELL':
        'Invalid ASIN in marketplace',
      'marketplaceFilterSetting.source.PRICED_BELOW_MIN':
        'Priced below minimum',
      'marketplaceFilterSetting.target.chineseSellerTarget':
        'Chinese Seller Target',
      'marketplaceFilterSetting.target.sellerCount': 'Seller Count',
      'marketplaceFilterSetting.target.min': 'Min',
      'marketplaceFilterSetting.target.max': 'Max',
      'marketplaceFilterSetting.target.priceDifference': 'Price Difference',
      'marketplaceFilterSetting.target.compareMethod': 'Compare Method',
      'marketplaceFilterSetting.target.buyBox': 'Buy Box',
      'marketplaceFilterSetting.target.lowestPrice': 'Lowest Price',
      'marketplaceFilterSetting.target.averagePrice': 'Average Price',
      'marketplaceFilterSetting.target.maxDifference': 'Max Difference (%)',
      'marketplaceFilterSetting.target.excludeLowestUnmatchable':
        'Exclude Lowest Unmatchable',
      'marketplaceFilterSetting.target.hasFbaSeller': 'Has FBA Seller',
      'marketplaceFilterSetting.target.brandSeller': 'Brand selling under its name',
      'marketplaceFilterSetting.target.sameSeller': 'Same Seller',
      'marketplaceFilterSetting.target.sbaSeller': 'SBA Seller',
      'marketplaceFilterSetting.target.SELLER_COUNT': 'Seller Count',
      'marketplaceFilterSetting.target.PRICE_DIFF': 'Price Difference',
      'marketplaceFilterSetting.target.SBA_SELLER': 'SBA Seller',
      'marketplaceFilterSetting.target.HAS_FBA_SELLER': 'Has FBA Seller',
      'marketplaceFilterSetting.target.BRAND_SELLER':
        'Brand selling under its name',
      // Product Search - Change Decision
      'productSearch.deleteSearch': 'Delete Search',
      'productSearch.cancelSearch': 'Cancel Search',
      'productSearch.changeDecision.title': 'Change Status',
      'productSearch.changeDecision.selectDecision': 'Select decision',
      'productSearch.changeDecision.noItems':
        'No products found to process.',
      'productSearch.changeDecision.success':
        'Decision for {{count}} search result(s) has been updated to {{decisionLabel}}.',
      'productSearch.changeDecision.submit': 'Update',
      'productSearch.changeDecision.cancel': 'Cancel',
      'productSearch.decision.SALEABLE': 'Saleable',
      'productSearch.decision.RECHECK': 'Recheck',
      'productSearch.decision.UNSALEABLE': 'Unsaleable',
      // Product History (EN)
      'productHistory.updateReason.SOURCE':
        'Source (Amazon.com) data changed',
      'productHistory.updateReason.SHIPPING': 'Shipping data changed',
      'productHistory.updateReason.COMPETITOR':
        'Competitor price/offer changed in target market',
      'productHistory.updateReason.OWN': 'Own price/stock changed',
      // Storefront scan filters (API status values shared with other enums)
      'search.status.PENDING': 'Pending',
      'search.status.INPROGRESS': 'In progress',
      'search.status.COMPLETED': 'Completed',
      'search.status.FAILED': 'Failed',
      'search.status.CANCELED': 'Canceled',
      'storefrontScan.status.PENDING': 'Pending',
      'storefrontScan.status.INPROGRESS': 'In progress',
      'storefrontScan.status.COMPLETED': 'Completed',
      'storefrontScan.status.FAILED': 'Will retry',
      'storefrontScan.status.SUSPENDED': 'Suspended',
      'storefrontScan.status.EXPORTED': 'Exported',
      'storefrontScan.status.UNAVALIABLE': 'Unavailable',
      'storefrontScan.status.WAITING_RESULTS': 'Waiting for results',
      // Product upload / inventory update history job status
      'productUploadHistory.jobStatus.READY': 'Ready',
      'productUploadHistory.jobStatus.PENDING': 'Pending',
      'productUploadHistory.jobStatus.SUBMITTED': 'Submitted',
      'productUploadHistory.jobStatus.PROCESSING': 'Processing',
      'productUploadHistory.jobStatus.COMPLETED': 'Completed',
      'productUploadHistory.jobStatus.SUCCEEDED': 'Succeeded',
      'productUploadHistory.jobStatus.FAILED': 'Failed',
      'productUploadHistory.jobStatus.CANCELLED': 'Cancelled',
      // Dashboard - Inventory Qualit
      'dashboard.accountStatus': 'Account Status',
      'dashboard.accountStatus.NORMAL': 'Normal',
      'dashboard.accountStatus.AT_RISK': 'At Risk',
      'dashboard.accountStatus.DEACTIVATED': 'Deactivated',
      'dashboard.accountStatus.UNKNOWN': 'Unknown',
      'dashboard.accountStatus.SUSPENDED_BY_USER': 'Suspended by User',
      'dashboard.onVacation': 'On vacation',
      // Product categories (Amazon) – keys must match enum values (DB format)
      'Amazon Luxury': 'Amazon Luxury',
      Appliances: 'Appliances',
      'Apps & Games': 'Apps & Games',
      'Arts, Crafts & Sewing': 'Arts, Crafts & Sewing',
      Automotive: 'Automotive',
      'Baby Products': 'Baby Products',
      'Beauty & Personal Care': 'Beauty & Personal Care',
      Books: 'Books',
      'CDs & Vinyl': 'CDs & Vinyl',
      'Cell Phones & Accessories': 'Cell Phones & Accessories',
      'Clothing, Shoes & Jewelry': 'Clothing, Shoes & Jewelry',
      'Collectibles & Fine Art': 'Collectibles & Fine Art',
      'Digital Music': 'Digital Music',
      Electronics: 'Electronics',
      'Everything Else': 'Everything Else',
      'Grocery & Gourmet Food': 'Grocery & Gourmet Food',
      'Health & Household': 'Health & Household',
      'Home & Kitchen': 'Home & Kitchen',
      'Industrial & Scientific': 'Industrial & Scientific',
      'Movies & TV': 'Movies & TV',
      'Musical Instruments': 'Musical Instruments',
      'Office Products': 'Office Products',
      'Patio, Lawn & Garden': 'Patio, Lawn & Garden',
      'Pet Supplies': 'Pet Supplies',
      Software: 'Software',
      Spine: 'Spine',
      'Sports & Outdoors': 'Sports & Outdoors',
      Stores: 'Stores',
      'Tools & Home Improvement': 'Tools & Home Improvement',
      'Toys & Games': 'Toys & Games',
      'Video Games': 'Video Games',
    },
  },
  tr: {
    translation: {
      [MarketplaceDTOMarketplaceEnum.Australia]: 'Avustralya',
      [MarketplaceDTOMarketplaceEnum.Brazil]: 'Brezilya',
      [MarketplaceDTOMarketplaceEnum.Canada]: 'Kanada',
      [MarketplaceDTOMarketplaceEnum.France]: 'Fransa',
      [MarketplaceDTOMarketplaceEnum.Germany]: 'Almanya',
      [MarketplaceDTOMarketplaceEnum.India]: 'Hindistan',
      [MarketplaceDTOMarketplaceEnum.Italy]: 'İtalya',
      [MarketplaceDTOMarketplaceEnum.Japan]: 'Japonya',
      [MarketplaceDTOMarketplaceEnum.Mexico]: 'Meksika',
      [MarketplaceDTOMarketplaceEnum.Spain]: 'İspanya',
      [MarketplaceDTOMarketplaceEnum.UnitedKingdom]: 'Birleşik Krallık',
      [MarketplaceDTOMarketplaceEnum.Netherlands]: 'Hollanda',
      [MarketplaceDTOMarketplaceEnum.Belgium]: 'Belçika',
      [MarketplaceDTOMarketplaceEnum.Egypt]: 'Mısır',
      [MarketplaceDTOMarketplaceEnum.Poland]: 'Polonya',
      [MarketplaceDTOMarketplaceEnum.SaudiArabia]: 'Suudi Arabistan',
      [MarketplaceDTOMarketplaceEnum.Singapore]: 'Singapur',
      [MarketplaceDTOMarketplaceEnum.Sweden]: 'İsveç',
      [MarketplaceDTOMarketplaceEnum.Ireland]: 'İrlanda',
      [MarketplaceDTOMarketplaceEnum.UnitedArabEmirates]:
        'Birleşik Arap Emirlikleri',
      [MarketplaceDTOMarketplaceEnum.UnitedStates]:
        'Amerika Birleşik Devletleri',
      welcome: 'Hoşgeldiniz',
      description: 'Bu bir örnektir',
      'search.status.PENDING': 'Beklemede',
      'search.status.INPROGRESS': 'Devam Ediyor',
      'search.status.COMPLETED': 'Tamamlandı',
      'search.status.FAILED': 'Hata',
      'search.status.CANCELED': 'İptal Edildi',
      'storefrontScan.status.PENDING': 'Beklemede',
      'storefrontScan.status.INPROGRESS': 'Devam Ediyor',
      'storefrontScan.status.COMPLETED': 'Tamamlandı',
      'storefrontScan.status.FAILED': 'Tekrar Denenecek',
      'storefrontScan.status.SUSPENDED': 'Askıya Alındı',
      'storefrontScan.status.EXPORTED': 'Aktarıldı',
      'storefrontScan.status.UNAVALIABLE': 'Kullanılamaz',
      'storefrontScan.status.WAITING_RESULTS': 'Sonuç Bekleniyor',
      'productUploadHistory.jobStatus.READY': 'Hazırlanıyor',
      'productUploadHistory.jobStatus.PENDING': 'Beklemede',
      'productUploadHistory.jobStatus.SUBMITTED': 'Gönderildi',
      'productUploadHistory.jobStatus.PROCESSING': 'İşleniyor',
      'productUploadHistory.jobStatus.COMPLETED': 'Tamamlandı',
      'productUploadHistory.jobStatus.SUCCEEDED': 'Başarılı',
      'productUploadHistory.jobStatus.FAILED': 'Hatalı',
      'productUploadHistory.jobStatus.CANCELLED': 'İptal Edildi',
      [GetStorefrontScansTagEnum.Closed]: 'Kapandı',
      [GetStorefrontScansTagEnum.Control]: 'Kontrol',
      [GetStorefrontScansTagEnum.Forbidden]: 'Yasaklı',
      [GetStorefrontScansLocationEnum.All]: 'Tümü',
      [InventoryDTOInventoryStatusEnum.Active]: 'Aktif',
      [InventoryDTOInventoryStatusEnum.Inactive]: 'Pasif',
      [InventoryDTOInventoryStatusEnum.Creating]: 'Oluşturuluyor',
      [InventoryDTOInventoryStatusEnum.Deleting]: 'Siliniyor',
      [InventoryDTOInventoryStatusEnum.Incomplete]: 'Eksik',
      Europe: 'Avrupa',
      Americas: 'Amerika',
      'Middle East': 'Orta Doğu',
      'Asia-Pacific': 'Asya-Pasifik',
      Oceania: 'Okyanusya',
      // Banned Categories Page
      'bannedCategories.categoryName': 'Kategori Adı',
      'bannedCategories.marketplace': 'Pazar Yeri',
      'bannedCategories.createdAt': 'Oluşturulma Tarihi',
      'bannedCategories.action': 'İşlem',
      'bannedCategories.remove': 'Sil',
      'bannedCategories.removeConfirm':
        'Bu kategoriyi kaldırmak istediğinizden emin misiniz?',
      'bannedCategories.removeSelectedConfirm':
        'Seçili {{count}} kategoriyi kaldırmak istediğinizden emin misiniz?',
      'bannedCategories.removeSelected': 'Seçileni Sil',
      'bannedCategories.yes': 'Evet',
      'bannedCategories.no': 'Hayır',
      'bannedCategories.filterByMarketplace': 'Pazar yerine göre filtrele',
      'bannedCategories.searchPlaceholder': 'Kategori ara',
      'bannedCategories.addCategories': 'Kategori Ekle',
      'bannedCategories.totalBannedCategories':
        'Toplam {{total}} yasaklı kategori',
      'bannedCategories.addBannedCategoriesInBulk': 'Toplu Kategori Ekle',
      'bannedCategories.categoriesOnePerLine': 'Kategoriler',
      'bannedCategories.categorySelectionHint':
        'Ana kategori o departmanı yasaklar. Alt kategori kendi ana kategorisindeki tam yolu yasaklar; daha derin ürünler de girer. Aynı ad başka departmanda serbest kalır.',
      'bannedCategories.unmappedTitle': 'Ağaçta karşılığı olmayan kayıtlar',
      'bannedCategories.unmappedHint':
        'Bunlar serbest metin olarak duruyor, ağaçta işaretli değiller. Kaldırma Kaydet ile yazılır.',
      'bannedCategories.save': 'Kaydet',
      'bannedCategories.discard': 'Vazgeç',
      'bannedCategories.saveHint': 'İşaretler Kaydet denene kadar yazılmaz.',
      'bannedCategories.unsavedChanges':
        '{{added}} eklenecek, {{removed}} kaldırılacak',
      'bannedCategories.saved': 'Kaydedildi',
      'bannedCategories.commonPoolHint':
        'Kaydet bu pazardaki ortak havuzu günceller.',
      'bannedCategories.commonPoolReadOnlyHint':
        'Ortak havuz salt okunur. Uygulamak için kategorileri kendi yasaklı listenize kopyalayın.',
      'bannedCategories.enterAtLeastOneCategory':
        'Lütfen en az bir kategori girin',
      'bannedCategories.bulkLimit':
        'Tek seferde en fazla {{max}} kayıt eklenebilir',
      'bannedCategories.categoryPlaceholder':
        'Kategori adlarını girin, her satıra bir\nÖrnek:\nElektronik\nSilahlar\nYasaklı Ürünler',
      'bannedCategories.selectMarketplaces': 'Pazar Yerlerini Seç',
      'bannedCategories.selectAtLeastOneMarketplace':
        'Lütfen en az bir pazar yeri seçin',
      'bannedCategories.selectMarketplacesPlaceholder':
        'Bu kategorileri uygulamak için pazar yerlerini seçin',
      'bannedCategories.cancel': 'İptal',
      'bannedCategories.successfullyAdded': '{{count}} adet eklendi',
      'bannedCategories.failedToAdd': 'Kategoriler eklenemedi',
      'bannedCategories.categoryRemovedSuccessfully':
        'Kategori başarıyla silindi',
      'bannedCategories.failedToRemoveCategory': 'Kategori silinemedi',
      'bannedCategories.selectCategoriesToDelete':
        'Lütfen silinecek kategorileri seçin',
      'bannedCategories.successfullyRemoved':
        '{{count}} kategori başarıyla silindi',
      'bannedCategories.failedToRemoveCategories': 'Kategoriler silinemedi',
      // Banned Brands Page
      'bannedBrands.categoryName': 'Marka Adı',
      'bannedBrands.marketplace': 'Pazar Yeri',
      'bannedBrands.createdAt': 'Oluşturulma Tarihi',
      'bannedBrands.action': 'İşlem',
      'bannedBrands.remove': 'Sil',
      'bannedBrands.removeConfirm':
        'Bu markayı kaldırmak istediğinizden emin misiniz?',
      'bannedBrands.removeSelectedConfirm':
        'Seçili {{count}} markayı kaldırmak istediğinizden emin misiniz?',
      'bannedBrands.removeSelected': 'Seçileni Sil',
      'bannedBrands.yes': 'Evet',
      'bannedBrands.no': 'Hayır',
      'bannedBrands.filterByMarketplace': 'Pazar yerine göre filtrele',
      'bannedBrands.searchPlaceholder': 'Marka ara',
      'bannedBrands.addCategories': 'Marka Ekle',
      'bannedBrands.totalBannedCategories': 'Toplam {{total}} yasaklı marka',
      'bannedBrands.addBannedCategoriesInBulk': 'Toplu Marka Ekle',
      'bannedBrands.categoriesOnePerLine': 'Markalar (her satıra bir)',
      'bannedBrands.enterAtLeastOneCategory': 'Lütfen en az bir marka girin',
      'bannedBrands.bulkLimit':
        'Tek seferde en fazla {{max}} kayıt eklenebilir',
      'bannedBrands.categoryPlaceholder':
        'Marka adlarını girin, her satıra bir\nÖrnek:\nSony\nApple\nNike',
      'bannedBrands.selectMarketplaces': 'Pazar Yerlerini Seç',
      'bannedBrands.selectAtLeastOneMarketplace':
        'Lütfen en az bir pazar yeri seçin',
      'bannedBrands.selectMarketplacesPlaceholder':
        'Bu markaları uygulamak için pazar yerlerini seçin',
      'bannedBrands.cancel': 'İptal',
      'bannedBrands.successfullyAdded': '{{count}} adet eklendi',
      'bannedBrands.failedToAdd': 'Markalar eklenemedi',
      'bannedBrands.categoryRemovedSuccessfully': 'Marka başarıyla silindi',
      'bannedBrands.failedToRemoveCategory': 'Marka silinemedi',
      'bannedBrands.selectCategoriesToDelete':
        'Lütfen silinecek markaları seçin',
      'bannedBrands.successfullyRemoved':
        '{{count}} marka başarıyla silindi',
      'bannedBrands.failedToRemoveCategories': 'Markalar silinemedi',
      // Banned ASIN Page
      'bannedAsin.categoryName': 'Ürün',
      'bannedAsin.marketplace': 'Pazar Yeri',
      'bannedAsin.createdAt': 'Oluşturulma Tarihi',
      'bannedAsin.action': 'İşlem',
      'bannedAsin.remove': 'Sil',
      'bannedAsin.removeConfirm':
        'Bu ürünü kaldırmak istediğinizden emin misiniz?',
      'bannedAsin.removeSelectedConfirm':
        'Seçili {{count}} ürünü kaldırmak istediğinizden emin misiniz?',
      'bannedAsin.removeSelected': 'Seçileni Sil',
      'bannedAsin.yes': 'Evet',
      'bannedAsin.no': 'Hayır',
      'bannedAsin.filterByMarketplace': 'Pazar yerine göre filtrele',
      'bannedAsin.searchPlaceholder': 'ASIN ara',
      'bannedAsin.addCategories': 'Ürün Ekle',
      'bannedAsin.totalBannedCategories': 'Toplam {{total}} yasaklı ürün',
      'bannedAsin.addBannedCategoriesInBulk': 'Toplu Ürün Ekle',
      'bannedAsin.categoriesOnePerLine': 'Ürünler (her satıra bir)',
      'bannedAsin.enterAtLeastOneCategory': 'Lütfen en az bir ürün girin',
      'bannedAsin.bulkLimit': 'Tek seferde en fazla {{max}} kayıt eklenebilir',
      'bannedAsin.categoryPlaceholder':
        'Ürün kodlarını girin, her satıra bir\nÖrnek:\nB08XYZ1234\nB09ABC5678\nB10DEF9012',
      'bannedAsin.selectMarketplaces': 'Pazar Yerlerini Seç',
      'bannedAsin.selectAtLeastOneMarketplace':
        'Lütfen en az bir pazar yeri seçin',
      'bannedAsin.selectMarketplacesPlaceholder':
        'Bu ürünlerı uygulamak için pazar yerlerini seçin',
      'bannedAsin.cancel': 'İptal',
      'bannedAsin.successfullyAdded': '{{count}} adet eklendi',
      'bannedAsin.failedToAdd': 'ürünler eklenemedi',
      'bannedAsin.categoryRemovedSuccessfully': 'ASIN başarıyla silindi',
      'bannedAsin.failedToRemoveCategory': 'ürün silinemedi',
      'bannedAsin.selectCategoriesToDelete': 'Lütfen silinecek ürünlerı seçin',
      'bannedAsin.successfullyRemoved': '{{count}} ürün başarıyla silindi',
      'bannedAsin.failedToRemoveCategories': 'ürünler silinemedi',
      // Banned Keywords Page
      'bannedKeyword.categoryName': 'Kelime',
      'bannedKeyword.marketplace': 'Pazar Yeri',
      'bannedKeyword.createdAt': 'Oluşturulma Tarihi',
      'bannedKeyword.action': 'İşlem',
      'bannedKeyword.remove': 'Sil',
      'bannedKeyword.removeConfirm':
        'Bu kelimeyi kaldırmak istediğinizden emin misiniz?',
      'bannedKeyword.removeSelectedConfirm':
        'Seçili {{count}} kelimeyi kaldırmak istediğinizden emin misiniz?',
      'bannedKeyword.removeSelected': 'Seçileni Sil',
      'bannedKeyword.yes': 'Evet',
      'bannedKeyword.no': 'Hayır',
      'bannedKeyword.filterByMarketplace': 'Pazar yerine göre filtrele',
      'bannedKeyword.searchPlaceholder': 'Kelime ara',
      'bannedKeyword.addCategories': 'Kelime Ekle',
      'bannedKeyword.totalBannedCategories': 'Toplam {{total}} yasaklı kelime',
      'bannedKeyword.addBannedCategoriesInBulk': 'Toplu Kelime Ekle',
      'bannedKeyword.categoriesOnePerLine': 'Kelimeler (her satıra bir)',
      'bannedKeyword.enterAtLeastOneCategory': 'Lütfen en az bir kelime girin',
      'bannedKeyword.bulkLimit': 'Tek seferde en fazla {{max}} kayıt eklenebilir',
      'bannedKeyword.categoryPlaceholder':
        'Kelimeleri girin, her satıra bir\nÖrnek:\nsilah\nbıçak\nkılıç',
      'bannedKeyword.selectMarketplaces': 'Pazar Yerlerini Seç',
      'bannedKeyword.selectAtLeastOneMarketplace':
        'Lütfen en az bir pazar yeri seçin',
      'bannedKeyword.selectMarketplacesPlaceholder':
        'Bu kelimeleri uygulamak için pazar yerlerini seçin',
      'bannedKeyword.cancel': 'İptal',
      'bannedKeyword.successfullyAdded': '{{count}} adet eklendi',
      'bannedKeyword.failedToAdd': 'Kelimeler eklenemedi',
      'bannedKeyword.categoryRemovedSuccessfully':
        'Kelime başarıyla silindi',
      'bannedKeyword.failedToRemoveCategory': 'Kelime silinemedi',
      'bannedKeyword.selectCategoriesToDelete':
        'Lütfen silinecek kelimeleri seçin',
      'bannedKeyword.successfullyRemoved':
        '{{count}} kelime başarıyla silindi',
      'bannedKeyword.failedToRemoveCategories': 'Kelimeler silinemedi',
      [SearchResultRequestDTODecisionEnum.Saleable]: 'Satılabilir',
      [SearchResultRequestDTODecisionEnum.Recheck]: 'Tekrar Kontrol',
      [SearchResultRequestDTODecisionEnum.Unsaleable]: 'Satılamaz',
      [SearchResultRequestDTODecisionEnum.Banned]: 'Yasaklı',
      [ListSearchesStatusesEnum.Pending]: 'Beklemede',
      [ListSearchesStatusesEnum.Inprogress]: 'Devam Ediyor',
      [ListSearchesStatusesEnum.Completed]: 'Tamamlandı',
      [ListSearchesStatusesEnum.Failed]: 'Hata',
      [ListSearchesStatusesEnum.Canceled]: 'İptal Edildi',
      // Storefront Page Tour (TR)
      'storefront.tour.start': 'Sayfa turu',
      'storefront.tour.filters.title': 'Filtreler',
      'storefront.tour.filters.desc':
        'Mağaza taramalarını lokasyon, etiket ve diğer alanlara göre filtreleyin.',
      'storefront.tour.savedFilters.title': 'Kayıtlı Filtreler',
      'storefront.tour.savedFilters.desc':
        'Kayıtlı filtrelerinizle hızlıca uygulama yapın veya yönetin.',
      'storefront.tour.newSearch.title': 'Yeni Arama',
      'storefront.tour.newSearch.desc':
        'Yeni bir mağaza taraması oluşturun. İlerlemeyi buradan takip edebilirsiniz.',
      'storefront.tour.bulkActions.title': 'Toplu İşlemler',
      'storefront.tour.bulkActions.desc':
        'Seçilen kayıtlar için kopyalama, indirme, aktarıldı, yeniden tarama, havuza ekleme veya silme işlemlerini yapın.',
      'storefront.tour.table.title': 'Sonuç Tablosu',
      'storefront.tour.table.desc':
        'Mağaza taramalarını görüntüleyin, mağaza sayfalarını açın ve satır işlemlerini kullanın.',
      // Search By Keyword Page Tour (TR)
      'searchByKeyword.tour.start': 'Sayfa turu',
      'searchByKeyword.tour.filters.title': 'Filtreler',
      'searchByKeyword.tour.filters.desc':
        'Anahtar kelime taramalarını ülke ve diğer parametrelere göre filtreleyin.',
      'searchByKeyword.tour.savedFilters.title': 'Kayıtlı Filtreler',
      'searchByKeyword.tour.savedFilters.desc':
        'Kayıtlı filtrelerinizi uygulayın veya yönetin.',
      'searchByKeyword.tour.newSearch.title': 'Yeni Arama',
      'searchByKeyword.tour.newSearch.desc':
        'Yeni bir anahtar kelime taraması oluşturun ve ilerlemeyi buradan takip edin.',
      'searchByKeyword.tour.bulkActions.title': 'Toplu İşlemler',
      'searchByKeyword.tour.bulkActions.desc':
        'Seçimler için kopyalama, indirme, aktarıldı, yeniden tarama, havuza ekleme veya silme işlemleri.',
      'searchByKeyword.tour.table.title': 'Sonuç Tablosu',
      'searchByKeyword.tour.table.desc':
        'Anahtar kelime tarama sonuçlarını görüntüleyin ve satır işlemlerini kullanın.',
      // Table pagination (TR)
      'table.pagination.total': 'Toplam {{total}} kayıt',
      // Marketplace Filter Setting Page (TR)
      'marketplaceFilterSetting.sourceFilters': 'Kaynak Filtreler',
      'marketplaceFilterSetting.targetCriteria': 'Hedef Kriterler',
      'marketplaceFilterSetting.save': 'Kaydet',
      'marketplaceFilterSetting.saveSuccess':
        'Yapılandırma başarıyla kaydedildi!',
      'marketplaceFilterSetting.saveError': 'Yapılandırma kaydedilemedi',
      'marketplaceFilterSetting.selectMarketplace':
        'Lütfen bir pazar yeri seçin',
      'marketplaceFilterSetting.source.POSSIBLE_DIGITAL_PRODUCT':
        'Olası Dijital Ürün',
      'marketplaceFilterSetting.source.UNKNOWN_SHIPPING_FEE':
        'Bilinmeyen Kargo Ücreti',
      'marketplaceFilterSetting.source.TRADEMARKED_PRODUCTS':
        'Marka Tescilli Ürünler',
      'marketplaceFilterSetting.source.SBA_TAGGED': 'SBA Etiketli',
      'marketplaceFilterSetting.source.FBA_TAGGED': 'FBA Etiketli',
      'marketplaceFilterSetting.source.AMAZON_REMOVED_PRODUCTS':
        'Amazon Tarafından Kaldırılan Ürünler',
      'marketplaceFilterSetting.source.UNSHIPPABLE_PRODUCTS':
        'Gönderilemeyen Ürünler',
      'marketplaceFilterSetting.source.NON_RETURNABLE': 'İade Edilemez',
      'marketplaceFilterSetting.source.FBM_TAGGED': 'FBM Etiketli',
      'marketplaceFilterSetting.source.BRAND_AND_SELLER_SAME':
        'Marka ve Satıcı Aynı',
      'marketplaceFilterSetting.source.BANNED': 'Yasaklı',
      'marketplaceFilterSetting.source.COMPETITION_STRONG': 'Güçlü Rekabet',
      'marketplaceFilterSetting.source.UNQUALIFIED_PRODUCT':
        'Niteliksiz Ürün',
      'marketplaceFilterSetting.source.NO_DIRECT_SELL':
        'Pazaryerinde geçersiz ASIN',
      'marketplaceFilterSetting.source.PRICED_BELOW_MIN':
        'Min fiyatın altında satış',
      'marketplaceFilterSetting.target.chineseSellerTarget':
        'Çinli Satıcı Hedefi',
      'marketplaceFilterSetting.target.sellerCount': 'Satıcı Sayısı',
      'marketplaceFilterSetting.target.min': 'Min',
      'marketplaceFilterSetting.target.max': 'Max',
      'marketplaceFilterSetting.target.priceDifference': 'Fiyat Farkı',
      'marketplaceFilterSetting.target.compareMethod': 'Karşılaştırma Yöntemi',
      'marketplaceFilterSetting.target.buyBox': 'Buy Box',
      'marketplaceFilterSetting.target.lowestPrice': 'En Düşük Fiyat',
      'marketplaceFilterSetting.target.averagePrice': 'Ortalama Fiyat',
      'marketplaceFilterSetting.target.maxDifference': 'Maksimum Fark (%)',
      'marketplaceFilterSetting.target.excludeLowestUnmatchable':
        'En Düşük Eşleşmeyeni Hariç Tut',
      'marketplaceFilterSetting.target.hasFbaSeller': 'FBA Satıcısı Var',
      'marketplaceFilterSetting.target.brandSeller': 'Marka İsmiyle Satış',
      'marketplaceFilterSetting.target.sameSeller': 'Aynı Satıcı',
      'marketplaceFilterSetting.target.sbaSeller': 'SBA Satıcısı',
      'marketplaceFilterSetting.target.SELLER_COUNT': 'Satıcı Sayısı',
      'marketplaceFilterSetting.target.PRICE_DIFF': 'Fiyat Farkı',
      'marketplaceFilterSetting.target.SBA_SELLER': 'SBA Satıcısı',
      'marketplaceFilterSetting.target.HAS_FBA_SELLER': 'FBA Satıcısı Var',
      'marketplaceFilterSetting.target.BRAND_SELLER': 'Marka İsmiyle Satış',
      'productSearch.deleteSearch': 'Aramayı Sil',
      'productSearch.cancelSearch': 'Aramayı İptal Et',
      'productSearch.changeDecision.title': 'Durum Değiştir',
      'productSearch.changeDecision.selectDecision': 'Karar seçin',
      'productSearch.changeDecision.noItems':
        'İşlem yapılacak ürün bulunamadı.',
      'productSearch.changeDecision.success':
        '{{count}} arama sonucunun karar durumu {{decisionLabel}} olarak güncellendi.',
      'productSearch.changeDecision.submit': 'Güncelle',
      'productSearch.changeDecision.cancel': 'İptal',
      'productSearch.decision.SALEABLE': 'Satılabilir',
      'productSearch.decision.RECHECK': 'Tekrar Kontrol',
      'productSearch.decision.UNSALEABLE': 'Satılamaz',
      // Product History (TR)
      'productHistory.updateReason.SOURCE':
        'Amazon.com (kaynak) verisi değişti',
      'productHistory.updateReason.SHIPPING': 'Kargo verisi değişti',
      'productHistory.updateReason.COMPETITOR':
        'Satış ülkesindeki rakip fiyat/teklif değişti',
      'productHistory.updateReason.OWN':
        'Kendi satış fiyatı veya stok bilgisi değişti',
      // Dashboard - Inventory Quality
      'dashboard.accountStatus': 'Hesap Durumu',
      'dashboard.accountStatus.NORMAL': 'Normal',
      'dashboard.accountStatus.AT_RISK': 'Riskli',
      'dashboard.accountStatus.DEACTIVATED': 'Aktif Değil',
      'dashboard.accountStatus.UNKNOWN': 'Bilinmiyor',
      'dashboard.accountStatus.SUSPENDED_BY_USER': 'Tatilde',
      'dashboard.onVacation': 'Tatilde',
      // Product categories (Amazon) - Turkish (keys = enum values / DB format)
      'Amazon Luxury': 'Amazon Lüks',
      Appliances: 'Beyaz Eşya',
      'Apps & Games': 'Uygulamalar ve Oyunlar',
      'Arts, Crafts & Sewing': 'Sanat, El Sanatları ve Dikiş',
      Automotive: 'Otomotiv',
      'Baby Products': 'Bebek Ürünleri',
      'Beauty & Personal Care': 'Güzellik ve Kişisel Bakım',
      Books: 'Kitaplar',
      'CDs & Vinyl': 'CD ve Plak',
      'Cell Phones & Accessories': 'Cep Telefonları ve Aksesuarlar',
      'Clothing, Shoes & Jewelry': 'Giyim, Ayakkabı ve Takı',
      'Collectibles & Fine Art': 'Koleksiyon ve Güzel Sanatlar',
      'Digital Music': 'Dijital Müzik',
      Electronics: 'Elektronik',
      'Everything Else': 'Diğer Her Şey',
      'Grocery & Gourmet Food': 'Bakkal ve Gurme Gıda',
      'Health & Household': 'Sağlık ve Ev',
      'Home & Kitchen': 'Ev ve Mutfak',
      'Industrial & Scientific': 'Endüstriyel ve Bilimsel',
      'Movies & TV': 'Filmler ve TV',
      'Musical Instruments': 'Müzik Aletleri',
      'Office Products': 'Ofis Ürünleri',
      'Patio, Lawn & Garden': 'Teras, Bahçe ve Çim',
      'Pet Supplies': 'Evcil Hayvan Ürünleri',
      Software: 'Yazılım',
      Spine: 'Spine',
      'Sports & Outdoors': 'Spor ve Outdoor',
      Stores: 'Mağazalar',
      'Tools & Home Improvement': 'Aletler ve Ev Geliştirme',
      'Toys & Games': 'Oyuncaklar ve Oyunlar',
      'Video Games': 'Video Oyunları',
    },
  },
};

// Initialize i18next
i18n.use(initReactI18next).init({
  resources,
  lng: 'tr', // default language
  fallbackLng: 'tr',
  interpolation: {
    escapeValue: false, // react already escapes by default
  },
});

export default i18n;
